import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { safeLocalStorageGet, safeLocalStorageSet, safeLocalStorageRemove } from '../utils/safeAccess';
import mixpanelService from '../utils/mixpanel';
import { prefetchOnboardingBootstrap, clearBootstrapCache } from '../utils/onboardingProgress';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

// Set the base URL for all API requests once at module load.
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
axios.defaults.baseURL = API_URL;

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const initialToken = safeLocalStorageGet('token');
  const [token, setToken] = useState(initialToken);
  const [loading, setLoading] = useState(!!initialToken); // Only load if there's a token
  const [error, setError] = useState(null);
  const [availabilityNotification, setAvailabilityNotification] = useState({
    show: false,
    message: ''
  });
  const [friendsNotification, setFriendsNotification] = useState({
    show: false,
    message: ''
  });
  const [preferencesNotification, setPreferencesNotification] = useState({
    show: false,
    message: ''
  });
  const [onboardingStatus, setOnboardingStatus] = useState({
    interview: false,
    quiz: false,
    profileComplete: false,
    waitlistHandoffComplete: false,
    resumeStepId: null,
    interviewDeferred: false,
    loading: true
  });

  // Add token to all requests if it exists
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  const logout = useCallback(() => {
    // Track logout event
    mixpanelService.track('User Logged Out');
    clearBootstrapCache();

    setToken(null);
    setCurrentUser(null);
    setOnboardingStatus({
      interview: false,
      quiz: false,
      profileComplete: false,
      waitlistHandoffComplete: false,
      resumeStepId: null,
      interviewDeferred: false,
      loading: false
    });
    setError(null);
    setAvailabilityNotification({ show: false, message: '' });
    setFriendsNotification({ show: false, message: '' });
    setPreferencesNotification({ show: false, message: '' });
    safeLocalStorageRemove('token');
    delete axios.defaults.headers.common['Authorization'];
    
    // Reset Mixpanel session
    mixpanelService.reset();
  }, []);

  const fetchCurrentUser = useCallback(async () => {
    try {
      setError(null);
      setOnboardingStatus(prev => ({ ...prev, loading: true }));
      const response = await axios.get('/api/auth/me');
      
      // Safely access response data
      const userData = response.data || {};
      // Extract user object from response (API returns {user: {...}})
      const user = userData.user || userData;
      setCurrentUser(user);

      const registrationProfileIncomplete = user?.registration_profile_completed === false;

      // Fetch onboarding status
      try {
        const onboardingRes = await axios.get('/api/onboarding/completion');
        const completionData = onboardingRes.data?.data || onboardingRes.data || {};
        const profileComplete = !!completionData.complete;
        const waitlistHandoffComplete = !!completionData.waitlist_handoff_complete;
        /** Required welcome = interview ∧ dinner; backend aligns both flags — require both defensively. */
        const isOnboardingIncomplete = !profileComplete || !waitlistHandoffComplete;
        setOnboardingStatus({
          interview: !!completionData.interview,
          quiz: !!completionData.quiz,
          profileComplete,
          waitlistHandoffComplete,
          resumeStepId: completionData.resume_step_id || null,
          interviewDeferred: !!completionData.interview_deferred,
          loading: false
        });

        // When onboarding is incomplete, start fetching bootstrap data and the
        // WelcomeOnboarding chunk in the background so the user never sees a
        // blank screen after PrivateRoute redirects to /welcome.
        // Skip until registration profile is complete — user is sent to /register first.
        if (isOnboardingIncomplete && !registrationProfileIncomplete) {
          const currentToken = safeLocalStorageGet('token');
          prefetchOnboardingBootstrap(currentToken, { cachedUser: user });
          import('../components/WelcomeOnboarding').catch(() => {});
        }
      } catch (onboardingErr) {
        console.error('Error fetching onboarding status:', onboardingErr);
        setOnboardingStatus(prev => ({ ...prev, loading: false }));
      }
    } catch (err) {
      console.error('Error fetching current user:', err);
      setError('Failed to fetch user data');
      
      // If token is invalid, clear it
      if (err.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  // Always sync from the API when we have a token. Login/register set currentUser from the
  // auth response, but onboarding completion is only loaded in fetchCurrentUser — skipping
  // that fetch when currentUser was already set left onboardingStatus.loading stuck true.
  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchCurrentUser]);

  const register = async (userData) => {
    try {
      setError(null);
      const response = await axios.post('/api/auth/register', userData);
      
      // Safely access response data
      const responseData = response.data || {};
      const { access_token: newToken, user } = responseData;
      
      if (!newToken || !user) {
        console.error('Invalid response format:', responseData);
        setError('Invalid response from server');
        return { 
          success: false, 
          error: 'Invalid response from server' 
        };
      }
      
      // Set token in localStorage first
      safeLocalStorageSet('token', newToken);
      
      // Set axios default header
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      // Set state
      setToken(newToken);
      setCurrentUser(user);
      
      // Initialize Mixpanel with user data
      mixpanelService.init(user.id, {
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        registration_date: new Date().toISOString(),
        user_type: 'registered'
      });
      
      return { success: true, user, access_token: newToken };
    } catch (err) {
      console.error('Registration error:', err);
      const errorMessage = err.response?.data?.error || 'Registration failed';
      setError(errorMessage);
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  const registerCredentials = async (payload) => {
    try {
      setError(null);
      const response = await axios.post('/api/auth/register/credentials', payload);
      const responseData = response.data || {};
      const { access_token: newToken, user } = responseData;
      if (!newToken || !user) {
        setError('Invalid response from server');
        return { success: false, error: 'Invalid response from server' };
      }
      safeLocalStorageSet('token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
      setCurrentUser(user);
      mixpanelService.init(user.id, {
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        registration_date: new Date().toISOString(),
        user_type: 'registered',
      });
      return { success: true, user, access_token: newToken };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Registration failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const registerGoogleCredentials = async (payload) => {
    try {
      setError(null);
      const response = await axios.post('/api/auth/register/google-credentials', payload);
      const responseData = response.data || {};
      const { access_token: newToken, user } = responseData;
      if (!newToken || !user) {
        setError('Invalid response from server');
        return { success: false, error: 'Invalid response from server' };
      }
      safeLocalStorageSet('token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
      setCurrentUser(user);
      mixpanelService.init(user.id, {
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        registration_date: new Date().toISOString(),
        user_type: 'registered',
        auth_method: 'google',
      });
      return { success: true, user, access_token: newToken };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Google registration failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const completeRegistrationProfile = async (profilePayload) => {
    try {
      setError(null);
      const response = await axios.patch('/api/auth/register/profile', profilePayload);
      const responseData = response.data || {};
      const { access_token: newToken, user } = responseData;
      if (!newToken || !user) {
        setError('Invalid response from server');
        return { success: false, error: 'Invalid response from server' };
      }
      safeLocalStorageSet('token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
      setCurrentUser(user);
      mixpanelService.init(user.id, {
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        registration_date: new Date().toISOString(),
        user_type: 'registered',
      });
      return { success: true, user, access_token: newToken };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to complete registration';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const login = async (credentials) => {
    try {
      setError(null);
      const response = await axios.post('/api/auth/login', credentials);
      
      // Safely access response data
      const responseData = response.data || {};
      const { access_token: newToken, user } = responseData;
      
      if (!newToken || !user) {
        console.error('Invalid response format:', responseData);
        setError('Invalid response from server');
        return { 
          success: false, 
          error: 'Invalid response from server' 
        };
      }
      
      // Set token in localStorage first
      safeLocalStorageSet('token', newToken);
      
      // Set axios default header
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      // Set state
      setToken(newToken);
      setCurrentUser(user);
      
      // Initialize Mixpanel with user data
      mixpanelService.init(user.id, {
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        login_date: new Date().toISOString(),
        user_type: 'returning'
      });
      
      return { success: true, user };
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = err.response?.data?.error || 'Login failed';
      setError(errorMessage);
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  const loginWithGoogle = async (idToken) => {
    try {
      setError(null);
      const response = await axios.post('/api/auth/google-login', { id_token: idToken });
      const responseData = response.data || {};

      if (responseData.needs_registration) {
        return {
          success: false,
          needsRegistration: true,
          googleProfile: responseData.google_profile || null,
        };
      }

      const { access_token: newToken, user } = responseData;
      if (!newToken || !user) {
        setError('Invalid response from server');
        return {
          success: false,
          error: 'Invalid response from server',
        };
      }

      safeLocalStorageSet('token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
      setCurrentUser(user);

      mixpanelService.init(user.id, {
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        login_date: new Date().toISOString(),
        user_type: 'returning',
        auth_method: 'google',
      });

      return { success: true, user };
    } catch (err) {
      console.error('Google login error:', err);
      const errorMessage = err.response?.data?.error || 'Google sign-in failed';
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage,
      };
    }
  };

  const acceptCurrentTerms = async () => {
    try {
      setError(null);
      const response = await axios.post('/api/terms/accept', { accepted: true });
      const responseData = response.data?.data || {};
      const nextTermsStatus = responseData.terms_status || null;

      if (nextTermsStatus) {
        setCurrentUser(prev => (
          prev
            ? {
                ...prev,
                terms_status: nextTermsStatus,
              }
            : prev
        ));
      } else {
        await fetchCurrentUser();
      }

      return {
        success: true,
        data: responseData,
      };
    } catch (err) {
      console.error('Terms acceptance error:', err);
      const errorMessage = err.response?.data?.error || 'Failed to accept the Terms of Use';
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  };

  const dismissAvailabilityNotification = () => {
    setAvailabilityNotification({ show: false, message: '' });
  };

  const dismissFriendsNotification = () => {
    setFriendsNotification({ show: false, message: '' });
  };

  const dismissPreferencesNotification = () => {
    setPreferencesNotification({ show: false, message: '' });
  };

  const value = {
    currentUser,
    token,
    loading,
    error,
    isAuthenticated: !!token && !!currentUser,
    register,
    registerCredentials,
    registerGoogleCredentials,
    completeRegistrationProfile,
    login,
    loginWithGoogle,
    acceptCurrentTerms,
    logout,
    refreshCurrentUser: fetchCurrentUser,
    clearError: () => setError(null),
    onboardingStatus,
    availabilityNotification,
    dismissAvailabilityNotification,
    friendsNotification,
    dismissFriendsNotification,
    preferencesNotification,
    dismissPreferencesNotification
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 
